import React, { useState, useEffect } from 'react';
import { Alert, Typography, Box } from '@mui/material';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import {
	handleError,
	handleSuccess,
	handleWarning,
	handleInfo,
} from '../../../helpers/utils';
import { SummaryModal } from './SummaryModal';
import { TrafficLightFormStep } from './TrafficLightFormStep';
import { ClientInformationStep } from './ClientInformationStep';
import { SalesValueStep } from './SalesValueStep';
import { ObservationsStep } from './ObservationsStep';
import { ProspectsStepper } from './ProspectsStepper';
import { ProspectsActions } from './ProspectsActions';
import { validateInput } from '../../../helpers/validateInput';
import { createCall } from '../../../actions/prospections';
import { format2Date } from '../../../helpers/hours';

const steps = [
	'Información del Cliente',
	'Contacto',
	'Valor de Venta y Motivo de No Compra',
	'Observaciones',
];

const initialForm = {
	reference: '',
	clientType: '',
	clientName: '',
	email: '',
	phone: '',
	action: '',
	trafficLightContact: '',
	date1: null,
	trafficLightAppointment: '',
	date2: null,
	trafficLightLead: '',
	date3: null,
	trafficLightQuote: '',
	date4: null,
	trafficLightClosing: '',
	date5: null,
	saleValue: '',
	reasonNoPurchase: '',
	observations: '',
};

export const AddProspect = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [activeStep, setActiveStep] = useState(0);
	const [form, setForm] = useState(initialForm);
	const [formTouched, setFormTouched] = useState({});
	const [showSummary, setShowSummary] = useState(false);
	const { user } = useSelector((state) => state);
	const navigate = useNavigate();

	// Full name
	const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`;

	// Update form with the user's full name
	useEffect(() => {
		if (user) {
			setForm((prevForm) => ({
				...prevForm,
				asesor: fullName,
			}));
		}
	}, [user]);

	// Updates the form stateand marks the specified field as touched
	const handleUpdateForm = (field, value) => {
		setFormTouched({ ...formTouched, [field]: true });
		setForm({ ...form, [field]: value });
	};

	// Validates that all requiredfields
	const validateAllFields = () => {
		const requiredFields = [
			'reference',
			'clientType',
			'clientName',
			'email',
			'phone',
			'action',
			'saleValue',
			'reasonNoPurchase',
		];

		// Check if any of the required fields is missing
		for (const field of requiredFields) {
			if (!form[field]) {
				return false;
			}
		}

		// Define pairs of traffic lights and dates that need to be validated
		const requiredPairs = [
			{ light: form.trafficLightContact, date: form.date1 },
			{ light: form.trafficLightAppointment, date: form.date2 },
			{ light: form.trafficLightLead, date: form.date3 },
			{ light: form.trafficLightQuote, date: form.date4 },
			{ light: form.trafficLightClosing, date: form.date5 },
		];

		// Check if any of the required pairs is missing
		const filledPairs = requiredPairs.filter(
			({ light, date }) => light && date
		);

		// If any of the required pairs is missing, return false
		if (filledPairs.length < 1) {
			return false;
		}

		return true;
	};

	// Validates fields for each specific step of the form
	const validateStepFields = (step) => {
		let isValid = true;

		switch (step) {
			case 0:
				// Validate fields in the Client Information step
				const clientInfoFields = [
					'clientName',
					'clientType',
					'phone',
					'reference',
					'email',
					'action',
				];
				// Check if any of the required fields is missing
				clientInfoFields.forEach((field) => {
					if (validateInput(field, form[field], form)) {
						isValid = false;
						handleWarning(
							'Faltan campos obligatorios en la Información del Cliente.'
						);
					}
				});
				break;

			case 1:
				// Validate traffic light and date pairs in the contact step
				const requiredPairs = [
					{ light: form.trafficLightContact, date: form.date1 },
					{ light: form.trafficLightAppointment, date: form.date2 },
					{ light: form.trafficLightLead, date: form.date3 },
					{ light: form.trafficLightQuote, date: form.date4 },
					{ light: form.trafficLightClosing, date: form.date5 },
				];

				// Check if at least one traffic light and date pair is complete
				const filledPairs = requiredPairs.filter(
					({ light, date }) => light && date
				);

				// If any of the required pairs is missing, return false
				if (filledPairs.length < 1) {
					handleWarning(
						'Debe proporcionar al menos un semáforo y su respectiva fecha en la sección de Contacto.'
					);
					isValid = false;
				}

				//  Check if there are any duplicate dates
				const incompletePairs = requiredPairs.filter(
					({ light, date }) => (light && !date) || (!light && date)
				);

				//  If there are any incomplete pairs, return false
				if (incompletePairs.length > 0) {
					handleWarning(
						'Asegúrese de que los campos de semáforo y fecha estén completos.'
					);
					isValid = false;
				}
				break;

			case 2:
				// Validate fields in the Sales Value and Reason for No Purchase step
				const saleValueError = validateInput(
					'saleValue',
					form.saleValue,
					form
				);
				// Reason for no purchase must be valid
				const reasonNoPurchaseError = validateInput(
					'reasonNoPurchase',
					form.reasonNoPurchase,
					form
				);

				if (saleValueError || reasonNoPurchaseError) {
					handleWarning(
						'Faltan campos obligatorios o el Valor de Venta no es válido.'
					);
					isValid = false;
				}
				break;

			case 3:
				// Validate observations field in the Observations step
				const observationsError = validateInput(
					'observations',
					form.observations,
					form
				);
				if (form.observations && observationsError) {
					handleWarning(
						'Error en las observaciones. Por favor, verifique el contenido.'
					);
					isValid = false;
				}
				break;

			default:
				break;
		}

		return isValid;
	};

	// Handle next step
	const handleNext = () => {
		if (validateStepFields(activeStep)) {
			setActiveStep((prevStep) => prevStep + 1);
		}
	};

	// Handle previous step
	const handleBack = () => {
		setActiveStep((prevStep) => prevStep - 1);
	};

	// Handles the cancel action
	const handleCancel = () => {
		setForm(initialForm);
		setActiveStep(0);
		navigate('/prospecciones');
	};

	// Handles form submission
	const handleSubmit = () => {
		// Validate all fields
		if (!validateAllFields()) {
			handleError(
				'Error en el formulario, verifica los campos obligatorios.'
			);
			return;
		}

		setIsLoading(true);

		// Clean the sale value field
		const cleanedSaleValue = parseFloat(
			form.saleValue.replace(/[^\d.-]/g, '')
		);

		// Prepare data for submission
		const newCallData = {
			reference: form.reference || null,
			crm_type: form.clientType || null,
			name: form.clientName || null,
			email: form.email || null,
			phone_number: form.phone || null,
			action: form.action || null,
			contact: form.trafficLightContact || null,
			appointment: form.trafficLightAppointment || null,
			lead: form.trafficLightLead || null,
			quotation: form.trafficLightQuote || null,
			closing: form.trafficLightClosing || null,
			sale_value: cleanedSaleValue || null,
			non_purchase: form.reasonNoPurchase || null,
			observations: form.observations || null,
			first_date: format2Date(form.date1, 'YYYY-MM-DD'),
			second_date: format2Date(form.date2, 'YYYY-MM-DD'),
			third_date: format2Date(form.date3, 'YYYY-MM-DD'),
			fourth_date: format2Date(form.date4, 'YYYY-MM-DD'),
			fifth_date: format2Date(form.date5, 'YYYY-MM-DD'),
		};

		// Send create call request
		createCall(newCallData)
			.then(() => {
				// console.log('createCall response:', response.data);
				handleSuccess('Prospección creada exitosamente.');
				setForm(initialForm);
				setFormTouched({});
				setActiveStep(0);
				setIsLoading(false);
				navigate('/prospecciones');
			})
			.catch((error) => {
				// Handle error response
				if (error.response) {
					if (error.response.data.email) {
						handleError(
							'El correo electrónico ya existe. Intenta con otro.'
						);
					} else {
						handleError(
							'Error al crear la prospección, por favor intenta nuevamente.'
						);
					}
					handleError('Error creating call:', error.response.data);
				} else {
					handleError('Error creating call:', error.message);
				}
				setIsLoading(false);
			});
	};

	// Display summary
	const handleShowSummary = () => {
		handleInfo('Verifique la información antes de proceder.');
		setShowSummary(true);
	};

	// Close summary
	const handleCloseSummary = () => {
		setShowSummary(false);
	};

	// Form steps
	const renderStepContent = (step) => {
		switch (step) {
			case 0:
				return (
					<ClientInformationStep
						form={form}
						handleUpdateForm={handleUpdateForm}
						fullName={fullName}
					/>
				);
			case 1:
				return (
					<TrafficLightFormStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			case 2:
				return (
					<SalesValueStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			case 3:
				return (
					<ObservationsStep
						form={form}
						handleUpdateForm={handleUpdateForm}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div style={{ padding: '25px' }}>
			<Stack
				direction='column'
				alignItems='flex-start'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 10px 0',
					width: '100%',
				}}
			>
				<Typography variant='h4' gutterBottom>
					Registro de nueva prospección
				</Typography>
				<Typography variant='body1' gutterBottom>
					Complete el formulario para registrar una nueva prospección
					comercial. Asegúrese de llenar todos los campos obligatorios
					para facilitar el seguimiento de la misma.
				</Typography>

				<Box mt={2} mb={2}>
					<Alert severity='info'>
						<b>
							<u>
								Proporcione la información más detallada posible
								sobre el cliente y la situación, esto ayudará a
								tomar mejores decisiones en el proceso de
								seguimiento.
							</u>
						</b>
					</Alert>
				</Box>
			</Stack>

			<ProspectsStepper activeStep={activeStep} steps={steps} />

			{renderStepContent(activeStep)}

			<ProspectsActions
				activeStep={activeStep}
				steps={steps}
				handleBack={handleBack}
				handleNext={handleNext}
				handleShowSummary={handleShowSummary}
				handleCancel={handleCancel}
				handleSubmit={handleSubmit}
				isLoading={isLoading}
			/>

			<SummaryModal
				open={showSummary}
				handleClose={handleCloseSummary}
				form={form}
			/>
		</div>
	);
};
